import { Button, Flex, TextField, Text } from "@radix-ui/themes";
import { useCallback, ChangeEventHandler } from "react";
import { PersonIcon, IdCardIcon, TokensIcon } from "@radix-ui/react-icons";
import Select, { SelectOption } from "@/components/shared/select";
import {
  ApiKeyAuth,
  AuthConfig,
  authTypes,
  authTypeToLabel,
  BasicAuth,
  BearerAuth,
  isApiKeyLocation,
  isAuthType,
} from "@/v2/entities/curl-request";

interface BasicAuthFormProps {
  authConfig?: BasicAuth;
  onAuthConfigChange: (authConfig: BasicAuth) => void;
}

function BasicAuthForm({
  authConfig = { type: "basic", user: "", password: "" },
  onAuthConfigChange,
}: BasicAuthFormProps) {
  const handleUserChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    onAuthConfigChange({ ...authConfig, user: event.target.value });
  };

  const handlePasswordChange: ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    onAuthConfigChange({ ...authConfig, password: event.target.value });
  };

  return (
    <Flex direction="column" gap="3">
      <Flex align="center" gap="3">
        <PersonIcon className="text-gray-9" />
        <Text size="2" weight="medium">
          Basic Authentication
        </Text>
      </Flex>

      <div>
        <Text size="2" weight="medium" className="mb-2 block">
          Username
        </Text>
        <TextField.Root
          size="2"
          placeholder="Enter username"
          value={authConfig?.type === "basic" ? authConfig.user : ""}
          onChange={handleUserChange}
        />
      </div>

      <div>
        <Text size="2" weight="medium" className="mb-2 block">
          Password
        </Text>
        <TextField.Root
          size="2"
          type="password"
          placeholder="Enter password"
          value={authConfig?.type === "basic" ? authConfig.password : ""}
          onChange={handlePasswordChange}
        />
      </div>
    </Flex>
  );
}

interface ApiKeyAuthFormProps {
  authConfig?: ApiKeyAuth;
  onAuthConfigChange: (authConfig: ApiKeyAuth) => void;
}

function ApiKeyAuthForm({
  authConfig = { type: "apikey", key: "", value: "", location: "header" },
  onAuthConfigChange,
}: ApiKeyAuthFormProps) {
  const handleKeyChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    onAuthConfigChange({ ...authConfig, key: event.target.value });
  };

  const handleValueChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    onAuthConfigChange({ ...authConfig, value: event.target.value });
  };

  const handleLocationChange = (newLocation: string) => {
    if (!isApiKeyLocation(newLocation)) {
      return;
    }

    onAuthConfigChange({
      ...authConfig,
      location: newLocation,
    });
  };

  return (
    <Flex direction="column" gap="3">
      <Flex align="center" gap="3">
        <IdCardIcon className="text-gray-9" />
        <Text size="2" weight="medium">
          API Key Authentication
        </Text>
      </Flex>

      <div>
        <Text size="2" weight="medium" className="mb-2 block">
          Key Name
        </Text>
        <TextField.Root
          size="2"
          placeholder="e.g., X-API-Key, Authorization"
          value={authConfig?.type === "apikey" ? authConfig.key : ""}
          onChange={handleKeyChange}
        />
      </div>

      <div>
        <Text size="2" weight="medium" className="mb-2 block">
          Key Value
        </Text>
        <TextField.Root
          size="2"
          type="password"
          placeholder="Enter API key"
          value={authConfig?.type === "apikey" ? authConfig.value : ""}
          onChange={handleValueChange}
        />
      </div>

      <div>
        <Text size="2" weight="medium" className="mb-2 block">
          Location
        </Text>
        <Select
          value={authConfig?.type === "apikey" ? authConfig.location : "header"}
          onChange={handleLocationChange}
          options={apiKeyLocationOptions}
          size="medium"
        />
      </div>
    </Flex>
  );
}

interface BearerAuthFormProps {
  authConfig?: BearerAuth;
  onAuthConfigChange: (authConfig: BearerAuth) => void;
}

function BearerAuthForm({
  authConfig = { type: "bearer", token: "" },
  onAuthConfigChange,
}: BearerAuthFormProps) {
  const handleTokenChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    onAuthConfigChange({ ...authConfig, token: event.target.value });
  };

  return (
    <Flex direction="column" gap="3">
      <Flex align="center" gap="3">
        <TokensIcon className="text-gray-9" />
        <Text size="2" weight="medium">
          Bearer Token Authentication
        </Text>
      </Flex>

      <div>
        <Text size="2" weight="medium" className="mb-2 block">
          Token
        </Text>
        <TextField.Root
          size="2"
          type="password"
          placeholder="Enter bearer token"
          value={authConfig?.type === "bearer" ? authConfig.token : ""}
          onChange={handleTokenChange}
        />
      </div>
    </Flex>
  );
}

interface AuthorizationProps {
  authConfig?: AuthConfig;
  onAuthConfigChange: (authConfig: AuthConfig | undefined) => void;
}

const authTypeOptions: SelectOption[] = authTypes.map((type) => ({
  label: authTypeToLabel[type],
  value: type,
}));

const apiKeyLocationOptions: SelectOption[] = [
  { label: "Header", value: "header" },
  { label: "Query Parameter", value: "query" },
];

export function Authorization({
  authConfig,
  onAuthConfigChange,
}: AuthorizationProps) {
  const handleClearAuth = useCallback(() => {
    onAuthConfigChange(undefined);
  }, [onAuthConfigChange]);

  const handleAuthTypeChange = useCallback(
    (value: string) => {
      if (!isAuthType(value)) {
        return;
      }

      switch (value) {
        case "basic":
          onAuthConfigChange({
            type: "basic",
            user: "",
            password: "",
          });
          break;
        case "apikey":
          onAuthConfigChange({
            type: "apikey",
            key: "",
            value: "",
            location: "header",
          });
          break;
        case "bearer":
          onAuthConfigChange({
            type: "bearer",
            token: "",
          });
          break;
      }
    },
    [onAuthConfigChange]
  );

  const handleBasicAuthChange = useCallback(
    (newConfig: Partial<BasicAuth>) => {
      if (authConfig?.type === "basic") {
        onAuthConfigChange({ ...authConfig, ...newConfig });
      }
    },
    [authConfig, onAuthConfigChange]
  );

  const handleApiKeyChange = useCallback(
    (newConfig: ApiKeyAuth) => {
      if (authConfig?.type === "apikey") {
        onAuthConfigChange({ ...authConfig, ...newConfig });
      }
    },
    [authConfig, onAuthConfigChange]
  );

  const handleBearerChange = useCallback(
    (newConfig: BearerAuth) => {
      if (authConfig?.type === "bearer") {
        onAuthConfigChange({ ...authConfig, ...newConfig });
      }
    },
    [authConfig, onAuthConfigChange]
  );

  return (
    <div className="h-full">
      <Flex direction="column" gap="4">
        <Flex justify="between" align="center">
          <Text size="3" weight="medium">
            Authorization
          </Text>
          {authConfig != null && (
            <Button
              size="2"
              variant="soft"
              color="red"
              onClick={handleClearAuth}
            >
              Clear Auth
            </Button>
          )}
        </Flex>

        <div className="border border-gray-6 rounded-md overflow-hidden shadow-sm p-4">
          <Flex direction="column" gap="4">
            <div>
              <Text size="2" weight="medium" className="mb-2 block">
                Authentication Type
              </Text>
              <Select
                value={authConfig?.type}
                onChange={handleAuthTypeChange}
                options={authTypeOptions}
                size="medium"
              />
            </div>

            {authConfig?.type === "basic" && (
              <BasicAuthForm
                authConfig={authConfig}
                onAuthConfigChange={handleBasicAuthChange}
              />
            )}

            {authConfig?.type === "apikey" && (
              <ApiKeyAuthForm
                authConfig={authConfig}
                onAuthConfigChange={handleApiKeyChange}
              />
            )}

            {authConfig?.type === "bearer" && (
              <BearerAuthForm
                authConfig={authConfig}
                onAuthConfigChange={handleBearerChange}
              />
            )}
          </Flex>
        </div>
      </Flex>
    </div>
  );
}
